import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../common/types';
import { TrialStatus, useTrialStatus } from './useTrialStatus';
import { useOpenPlans } from './useOpenPlans';
import { useTrialPlanName } from './useTrialPlanName';
import { trialStatusMessages as messages } from './trialStatusMessages';
import { usePillWatermark } from '../watermarkUpsell/trial/usePillWatermark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import upsellStyles from '../watermarkUpsell/WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the pill and the chip; each uses its own slice of it */
import styles from './TrialStatus.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TrialStatusPillProps {
  /** The collapsed rail: the tile alone, with a tooltip. */
  isRail?: boolean;
}

interface PillCopy {
  tile: string;
  title: string;
  line: string;
}

function usePillCopy(status: TrialStatus | null): PillCopy | null {
  const intl = useIntl();
  const plan = useTrialPlanName();
  if (status === null) {
    return null;
  }
  switch (status.kind) {
    case 'running':
      return {
        tile: String(status.days),
        title: intl.formatMessage(messages.runningTitle, { plan }),
        line: intl.formatMessage(status.hasSong ? messages.runningLine : messages.noSongLine),
      };
    case 'lastDay':
      return {
        tile: String(Math.max(status.days, 1)),
        title: intl.formatMessage(messages.lastDayTitle, { plan }),
        line: intl.formatMessage(messages.lastDayLine),
      };
    case 'ended':
      return {
        tile: intl.formatMessage(messages.endedTile),
        title: intl.formatMessage(messages.endedTitle),
        line: intl.formatMessage(messages.endedLine),
      };
  }
}

/**
 * The trial status in the sidebar bottom, above the checklist pill, on every page (design:
 * onboarding h3). A click opens the plan picker. It renders nothing for a member or without a trial.
 * With 1 or more watermarked files the second line is their count and the click opens the offer
 * (design: trial-upsell b1).
 */
const TrialStatusPillComponent: React.FC<TrialStatusPillProps> = ({ isRail = false }) => {
  const status = useTrialStatus();
  const copy = usePillCopy(status);
  const openPlans = useOpenPlans();
  const watermark = usePillWatermark(status?.kind ?? null);

  if (status === null || copy === null) {
    return null;
  }
  const line = watermark?.line ?? copy.line;

  return (
    <button
      type="button"
      className={clsx(styles.pill, status.kind === 'lastDay' && styles.warn, isRail && styles.rail)}
      onClick={watermark?.open ?? openPlans}
      title={isRail ? `${copy.title} · ${line}` : undefined}
      data-testid="trial-status-pill"
    >
      <span className={clsx(styles.tile, status.kind === 'ended' && styles.tileSmall)}>{copy.tile}</span>
      <span className={styles.text}>
        <b>{copy.title}</b>
        <small>{line}</small>
        {watermark !== null && <span className={upsellStyles.pillGo}>{watermark.action}</span>}
      </span>
      <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
    </button>
  );
};

export const TrialStatusPill = TypedMemo(TrialStatusPillComponent);
